import React, { useState, useEffect, useMemo } from "react"
import { Form, Input, Button, message as antdMessage, Spin, Select, Checkbox } from "antd"
import { useNavigate, useSearchParams } from "react-router-dom"
import usePasswordValidation from "../../utils/PasswordCheck"
import { authApi } from "../../Apis/authApis"
import { translations } from "../../utils/Translation/staticTranslation"
import ToggleLogin from "../../components/Common/ToggleLogin"
import useLanguage from "../../services/useLanguage"
import TermsOfServiceModal from "../../components/Common/Modals/TermsOfServiceModal"
import PrivacyPolicyModal from "../../components/Common/Modals/PrivacyPolicyModal"
import { getMarketingWebsiteUrl } from "../../utils/getMarketingWebsiteUrl"
import { MERCHANT_INDUSTRY_FILTERS } from "../../utils/merchantIndustryFilters"
import { getRegistrationCountryOptions } from "../../utils/registrationCountries"

const Signup = () => {
  const [form] = Form.useForm()
  const navigate = useNavigate()
  const [step, setStep] = useState(1)
  const [searchParams] = useSearchParams()

  const rawEmail = searchParams.get("email")
  const email = rawEmail
    ? decodeURIComponent(rawEmail)
        .replace(/ /g, "+")
        .replace(/\+[^@]+(?=@)/, "")
        .toLowerCase()
    : ""

  const userName = searchParams.get("name")
  const userPhoneNumber = searchParams.get("number")
  const role = searchParams.get("role")
  const token = searchParams.get("token")

  const { language, handleLanguageChange } = useLanguage()
  const [formData, setFormData] = useState({})
  const [loading, setLoading] = useState(false)
  const [tokenValid, setTokenValid] = useState(true)

  const { passwordChecked, validatePassword } = usePasswordValidation()
  const [isTermsModalOpen, setIsTermsModalOpen] = useState(false)
  const [isPrivacyModalOpen, setIsPrivacyModalOpen] = useState(false)
  const [industryOther, setIndustryOther] = useState(false)
  const [platformOther, setPlatformOther] = useState(false)
  const lang = localStorage.getItem("language") || "ar"
  const signup = translations[lang]
  const signup1 = translations[lang]?.signup

  const { common_error_msg } = translations[lang]
  const signupPage = translations[lang]?.signup
  const password = translations[lang]?.reset_password
  const loginPage = translations[lang]?.loginPage
  const websiteUrl = getMarketingWebsiteUrl(lang)
  const countryOptions = useMemo(
    () => getRegistrationCountryOptions(lang),
    [lang]
  )

  // Set initial values
  useEffect(() => {
    const initialValues = {
      email: email || "",
      vendor_name_english: userName || "",
      phone: userPhoneNumber || "",
    }
    form.setFieldsValue(initialValues)
  }, [email, userName, userPhoneNumber, form])

  // Validate token if present
  useEffect(() => {
    const validateSignupToken = async () => {
      if (token) {
        try {
          setLoading(true)
          const res = await authApi.validateToken(token, role)
          if (res?.status === 200) {
            setTokenValid(true)
          } else {
            setTokenValid(false)
          }
        } catch (err) {
          console.error("Token validation failed:", err)
          setTokenValid(false)
        } finally {
          setLoading(false)
        }
      }
    }

    validateSignupToken()
  }, [token, role])

  const onFinish = async (values) => {
    if (step === 1) {
      handleNextStep()
      return
    }

    if (!passwordChecked.lengthCheck || !passwordChecked.otherChecks) {
      return antdMessage.error(common_error_msg?.password_not_meet_requirements)
    }

    try {
      setLoading(true)
      const payload = {
        ...formData,
        password: values.password,
        confirm_password: values.confirm_password,
        token: token,
        role: role,
        language: lang === "ar" ? "AR" : "EN",
      }

      const response = await authApi.signUp(payload)
      if (response.status === 201) {
        form.resetFields()
        navigate("/login")
      }
    } catch (error) {
      const fieldErrors = error.response?.data
      const countryError = Array.isArray(fieldErrors?.country)
        ? fieldErrors.country[0]
        : null
      const fallbackMessage =
        lang === "en"
          ? fieldErrors?.error
          : fieldErrors?.errorArabic

      antdMessage.error(countryError || fallbackMessage)
      console.error("Error:", error)
    } finally {
      setLoading(false)
    }
  }

  const handleNextStep = () => {
    const formValues = form.getFieldsValue()

    // Block progress if email contains "+"
    if (formValues.email?.includes("+")) {
      antdMessage.error(
        language === "ar"
          ? 'لا يمكن أن يحتوي البريد الإلكتروني على "+"'
          : 'Email cannot contain "+"'
      )
      return
    }

    const requiredFields = [
      formValues.vendor_name_english,
      formValues.email,
      formValues.phone,
      formValues.business_name_english,
      formValues.industry,
      industryOther ? formValues.industry_other : true,
      formValues.ecommerce_platform,
      platformOther ? formValues.ecommerce_platform_other : true,
      formValues.country,
      formValues.city,
    ]

    setFormData({
      vendor_name_english: formValues.vendor_name_english || "",
      vendor_name_arabic: formValues.vendor_name_arabic || "",
      email: formValues.email.toLowerCase(),
      phone: formValues.phone,
      business_name_english: formValues.business_name_english || "",
      business_name_arabic: formValues.business_name_arabic || "",
      industry: formValues.industry || "",
      ...(industryOther && { industry_other: formValues.industry_other || "" }),
      ecommerce_platform: formValues.ecommerce_platform || "",
      ...(platformOther && { ecommerce_platform_other: formValues.ecommerce_platform_other || "" }),
      country: formValues.country || "",
      city: formValues.city || "",
    })

    const isFormComplete = requiredFields.every(
      (field) => field !== undefined && field !== ""
    )

    if (isFormComplete) {
      setStep(2)
    } else {
      antdMessage.error(
        language === "ar"
          ? "يرجى ملء جميع الحقول المطلوبة قبل المتابعة"
          : "Please fill in all required fields before proceeding"
      )
    }
  }

  const handleEmailChange = (e) => {
    if (!email) {
      const value = e.target.value.toLowerCase()
      form.setFieldsValue({ email: value })
    }
  }

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/[^0-9]/g, "")
    form.setFieldsValue({ phone: value })
  }

  if (!tokenValid) {
    return (
      <div className="login-bg-img">
        <div className="login-container">
          <div className="signup-inner">
            <div className="signup-left">
              <div className="signup-left-logo">
                <a href={websiteUrl}><img src="/signup/Zory brown.svg" alt="Zory Logo" /></a>
              </div>
            </div>
            <div className="signup-right">
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  height: "100%",
                  textAlign: "center",
                  fontSize: "24px",
                  fontWeight: "600",
                  color: "#343c3e",
                }}
              >
                {signup?.signup?.invalid_link}
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <>
      <TermsOfServiceModal
        isOpen={isTermsModalOpen}
        onClose={() => setIsTermsModalOpen(false)}
        translations={translations}
        currentLang={lang}
      />
      <PrivacyPolicyModal
        isOpen={isPrivacyModalOpen}
        onClose={() => setIsPrivacyModalOpen(false)}
        translations={translations}
        currentLang={lang}
      />
      <div className="signup-inner">
        <div className="signup-left">
          <div className="signup-left-logo">
            <a href={websiteUrl}><img src="/signup/Zory brown.svg" alt="Zory Logo" /></a>
          </div>
        </div>

        <div className="signup-right">
          <div className="sign-l-header" dir={lang === "ar" ? "rtl" : "ltr"}>
            <div className="back-arrow-inv">
              {step === 2 && (
                // <div
                //   className="d-flex back align-items-center gap-2 pointer"
                //   onClick={() => setStep(1)}
                //   style={{ cursor: "pointer" }}
                // >
                //   {language === "ar" ? (
                //     <img
                //       height={10}
                //       src="/icons/arrowSelectionDark.svg"
                //       alt=""
                //     />
                //   ) : (
                //     <img height={10} src="/icons/backarrow.svg" alt="" />
                //   )}
                //   <span className="text-14 text-141414 mb-0-imp">
                //     {signup?.signup?.back}
                //   </span>
                // </div>

                <div className="back-arrow" onClick={() => setStep(1)}>
                  <img
                    src="/signup/arrowBack.svg"
                    alt=""
                    className={lang === "ar" ? "rotated-arrow" : ""}
                  />
                  {lang === "ar" ? "رجوع" : "Back"}
                </div>
              )}
            </div>
            <div className="logo-sign">
              <a href={websiteUrl}><img src="/signup/Zorysmall.svg" alt="Zory Logo" /></a>
            </div>
            <div className="lang-toggle">
              <ToggleLogin
                toggleLanguage={handleLanguageChange}
                currentLanguage={language}
              />
            </div>
          </div>

          <div className="content">
            <h3 className="font-Montserrat00">
              {signup1?.createAccount}
              {/* {step === 1
                ? lang === "ar"
                  ? "إنشاء حساب جديد"
                  : "Create New Account"
                : lang === "ar"
                ? "إنشاء كلمة المرور"
                : "Create Password"} */}
            </h3>
            <span className="font-Montserrat-300">
              {/* {step === 1
                ? lang === "ar"
                  ? "يرجى إكمال بياناتك لإنشاء حسابك"
                  : "Please complete your details to create your account"
                : lang === "ar"
                ? "يرجى إنشاء كلمة مرور قوية لحسابك"
                : "Please create a strong password for your account"} */}
              {signup1?.enterDetails}
            </span>
          </div>

          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            style={{ marginTop: "24px" }}
          >
            {step === 1 ? (
              <>
                {/* Row 1: Vendor Names */}
                <div style={{ display: "flex", gap: "16px" }}>
                  <Form.Item
                    label={<span style={{ fontSize: "13px" }}>Full Name</span>}
                    name="vendor_name_english"
                    rules={[
                      {
                        required: true,
                        message: "Full name is required",
                      },
                    ]}
                    labelCol={{ span: 24 }}
                    style={{ flex: 1 }}
                  >
                    <Input
                      className="custom-input"
                      placeholder="Enter full name"
                      style={{
                        background: "#ffffff",
                        border: "1px solid rgba(132, 135, 136, 0.25)",
                        height: 50,
                        borderRadius: 12,
                        direction: "ltr",
                        textAlign: "left",
                      }}
                    />
                  </Form.Item>

                  <Form.Item
                    label={<label className="ar-label">الاسم الكامل</label>}
                    name="vendor_name_arabic"
                    labelCol={{ span: 24 }}
                    style={{
                      flex: 1,
                      direction: "rtl",
                      textAlign: "right",
                    }}
                  >
                    <Input
                      className="custom-input"
                      placeholder="الاسم الكامل"
                      style={{
                        background: "#ffffff",
                        border: "1px solid rgba(132, 135, 136, 0.25)",
                        height: 50,
                        borderRadius: 12,
                        direction: "rtl",
                        textAlign: "right",
                      }}
                    />
                  </Form.Item>
                </div>

                {/* Row 2: Email and Phone */}
                <div style={{ display: "flex", gap: "16px" }}>
                  <Form.Item
                    label={
                      <span className={lang === "ar" ? "ar-label" : ""}>
                        {lang === "ar" ? "بريد إلكتروني" : "Email"}
                      </span>
                    }
                    name="email"
                    rules={[
                      {
                        required: true,
                        message:
                          lang === "ar"
                            ? "البريد الإلكتروني مطلوب"
                            : "Email is required",
                      },
                      {
                        type: "email",
                        message:
                          lang === "ar"
                            ? "صيغة البريد الإلكتروني غير صحيحة"
                            : "Invalid email format",
                      },
                      {
                        validator: (_, value) =>
                          value && value.includes("+")
                            ? Promise.reject(
                                lang === "ar"
                                  ? 'لا يمكن أن يحتوي البريد الإلكتروني على "+"'
                                  : 'Email cannot contain "+"'
                              )
                            : Promise.resolve(),
                      },
                    ]}
                    labelCol={{ span: 24 }}
                    style={{ flex: 1 }}
                  >
                    <Input
                      className={
                        !!email ? "custom-input-disabled" : "custom-input"
                      }
                      placeholder={
                        lang === "ar"
                          ? "أدخل بريدك الإلكتروني"
                          : "Enter your email"
                      }
                      onChange={handleEmailChange}
                      disabled={true} // Always disabled like in your original SignUpStep1
                      style={{
                        background: "#f5f5f5", // Always disabled styling
                        border: "1px solid rgba(132, 135, 136, 0.25)",
                        height: 50,
                        borderRadius: 12,
                        color: "#8c8c8c", // Always disabled color
                        textTransform: "lowercase",
                      }}
                    />
                  </Form.Item>

                  <Form.Item
                    label={
                      <span className={lang === "ar" ? "ar-label" : ""}>
                        {lang === "ar" ? "رقم الهاتف" : "Phone Number"}
                      </span>
                    }
                    name="phone"
                    rules={[
                      {
                        required: true,
                        message:
                          signup?.phoneNumberMessage ||
                          (lang === "ar"
                            ? "رقم الهاتف مطلوب"
                            : "Phone number is required"),
                      },
                      {
                        min: 8,
                        message:
                          signup?.phone_number_error_2 ||
                          (lang === "ar"
                            ? "رقم الهاتف يجب أن يكون 8 أرقام على الأقل"
                            : "Phone number must be at least 8 digits"),
                      },
                    ]}
                    labelCol={{ span: 24 }}
                    style={{ flex: 1 }}
                  >
                    <Input
                      className="custom-input"
                      placeholder={
                        signup?.phoneNumberPlaceholder ||
                        (lang === "ar"
                          ? "أدخل رقم الهاتف"
                          : "Enter phone number")
                      }
                      onChange={handlePhoneChange}
                      style={{
                        background: "#ffffff",
                        border: "1px solid rgba(132, 135, 136, 0.25)",
                        height: 50,
                        borderRadius: 12,
                      }}
                    />
                  </Form.Item>
                </div>

                {/* Row 3: Business Names */}
                <div style={{ display: "flex", gap: "16px" }}>
                  <Form.Item
                    label={
                      <span style={{ fontSize: "13px" }}>
                        Store / Business Name
                      </span>
                    }
                    name="business_name_english"
                    rules={[
                      {
                        required: true,
                        message: "Store/Business name is required",
                      },
                    ]}
                    labelCol={{ span: 24 }}
                    style={{ flex: 1 }}
                  >
                    <Input
                      className="custom-input"
                      placeholder="Enter store or business name"
                      style={{
                        background: "#ffffff",
                        border: "1px solid rgba(132, 135, 136, 0.25)",
                        height: 50,
                        borderRadius: 12,
                        direction: "ltr",
                        textAlign: "left",
                      }}
                    />
                  </Form.Item>

                  <Form.Item
                    label={
                      <span className="ar-label">
                        المتجر/اسم النشاط التجاري
                      </span>
                    }
                    name="business_name_arabic"
                    labelCol={{ span: 24 }}
                    style={{
                      flex: 1,
                      direction: "rtl",
                    }}
                  >
                    <Input
                      className="custom-input"
                      placeholder="أدخل اسم العمل باللغة العربية"
                      style={{
                        background: "#ffffff",
                        border: "1px solid rgba(132, 135, 136, 0.25)",
                        height: 50,
                        borderRadius: 12,
                        direction: "rtl",
                        textAlign: "right",
                      }}
                    />
                  </Form.Item>
                </div>

                {/* Row 4: Industry and E-commerce Platform */}
                <div style={{ display: "flex", gap: "16px" }}>
                  <div style={{ flex: 1 }}>
                    <Form.Item
                      label={
                        <span className={lang === "ar" ? "ar-label" : ""} style={{ fontSize: "13px" }}>
                          {signup1?.industry || (lang === "ar" ? "القطاع" : "Industry")}
                        </span>
                      }
                      name="industry"
                      rules={[{ required: true, message: signup1?.industryRequired || (lang === "ar" ? "القطاع مطلوب" : "Industry is required") }]}
                      labelCol={{ span: 24 }}
                    >
                      <Select
                        placeholder={signup1?.industryPlaceholder || (lang === "ar" ? "اختر القطاع" : "Select Industry")}
                        style={{ height: 50, borderRadius: 12 }}
                        className="custom-select"
                        onChange={(val) => {
                          setIndustryOther(val === "Other")
                          if (val !== "Other") form.setFieldValue("industry_other", undefined)
                        }}
                      >
                        {MERCHANT_INDUSTRY_FILTERS.map((item) => (
                          <Select.Option key={item.value} value={item.value}>
                            {lang === "ar" ? item.label_ar : item.label_en}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                    {industryOther && (
                      <Form.Item
                        name="industry_other"
                        rules={[{ required: true, message: signup1?.industrySpecify || (lang === "ar" ? "يرجى تحديد القطاع" : "Please specify your industry") }]}
                        labelCol={{ span: 24 }}
                        style={{ marginTop: "-8px" }}
                      >
                        <Input
                          className="custom-input"
                          placeholder={signup1?.pleaseSpecify || (lang === "ar" ? "يرجى التحديد" : "Please specify")}
                          style={{ background: "#ffffff", border: "1px solid rgba(132, 135, 136, 0.25)", height: 50, borderRadius: 12 }}
                        />
                      </Form.Item>
                    )}
                  </div>

                  <div style={{ flex: 1 }}>
                    <Form.Item
                      label={
                        <span className={lang === "ar" ? "ar-label" : ""} style={{ fontSize: "13px" }}>
                          {signup1?.ecommercePlatform || (lang === "ar" ? "منصة التجارة الإلكترونية" : "E-commerce Platform")}
                        </span>
                      }
                      name="ecommerce_platform"
                      rules={[{ required: true, message: signup1?.ecommercePlatformRequired || (lang === "ar" ? "منصة التجارة الإلكترونية مطلوبة" : "E-commerce platform is required") }]}
                      labelCol={{ span: 24 }}
                    >
                      <Select
                        placeholder={signup1?.ecommercePlatformPlaceholder || (lang === "ar" ? "اختر المنصة" : "Select Platform")}
                        style={{ height: 50, borderRadius: 12 }}
                        className="custom-select"
                        onChange={(val) => {
                          setPlatformOther(val === "Other")
                          if (val !== "Other") form.setFieldValue("ecommerce_platform_other", undefined)
                        }}
                      >
                        {Object.entries(signup?.platformOptions || {}).map(([key, label]) => (
                          <Select.Option key={key} value={key}>
                            {label}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                    {platformOther && (
                      <Form.Item
                        name="ecommerce_platform_other"
                        rules={[{ required: true, message: signup1?.platformSpecify || (lang === "ar" ? "يرجى تحديد المنصة" : "Please specify your platform") }]}
                        labelCol={{ span: 24 }}
                        style={{ marginTop: "-8px" }}
                      >
                        <Input
                          className="custom-input"
                          placeholder={signup1?.pleaseSpecify || (lang === "ar" ? "يرجى التحديد" : "Please specify")}
                          style={{ background: "#ffffff", border: "1px solid rgba(132, 135, 136, 0.25)", height: 50, borderRadius: 12 }}
                        />
                      </Form.Item>
                    )}
                  </div>
                </div>

                {/* Row 5: Country and City */}
                <div style={{ display: "flex", gap: "16px" }}>
                  <Form.Item
                    label={
                      <span className={lang === "ar" ? "ar-label" : ""} style={{ fontSize: "13px" }}>
                        {signup1?.country || (lang === "ar" ? "البلد" : "Country")}
                      </span>
                    }
                    name="country"
                    rules={[{ required: true, message: signup1?.countryRequired || (lang === "ar" ? "البلد مطلوب" : "Country is required") }]}
                    labelCol={{ span: 24 }}
                    style={{ flex: 1 }}
                  >
                    <Select
                      showSearch
                      placeholder={signup1?.countryPlaceholder || (lang === "ar" ? "اختر البلد" : "Select your country")}
                      style={{ height: 50, borderRadius: 12 }}
                      className="custom-select"
                      optionFilterProp="label"
                      options={countryOptions}
                    />
                  </Form.Item>

                  <Form.Item
                    label={
                      <span className={lang === "ar" ? "ar-label" : ""} style={{ fontSize: "13px" }}>
                        {signup1?.city || (lang === "ar" ? "المدينة" : "City")}
                      </span>
                    }
                    name="city"
                    rules={[{ required: true, message: signup1?.cityRequired || (lang === "ar" ? "المدينة مطلوبة" : "City is required") }]}
                    labelCol={{ span: 24 }}
                    style={{ flex: 1 }}
                  >
                    <Input
                      className="custom-input"
                      placeholder={signup1?.cityPlaceholder || (lang === "ar" ? "أدخل المدينة" : "Enter your city")}
                      style={{ background: "#ffffff", border: "1px solid rgba(132, 135, 136, 0.25)", height: 50, borderRadius: 12 }}
                    />
                  </Form.Item>
                </div>

                <Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    style={{
                      width: "100%",
                      fontSize: "18px",
                      height: 50,
                      borderRadius: 12,
                      background: "#1d869a",
                      border: "none",
                    }}
                  >
                    {language === "ar" ? "التالي" : "Next"}
                  </Button>
                </Form.Item>
              </>
            ) : (
              <>
                {/* Step 2: Password */}
                <Form.Item
                  label={
                    <span className={lang === "ar" ? "ar-label" : ""}>
                      {lang === "ar" ? "كلمة المرور" : "Password"}
                    </span>
                  }
                  name="password"
                  rules={[
                    {
                      required: true,
                      message: password?.password_error,
                    },
                    {
                      min: 8,
                      message: password?.validation_2,
                    },
                  ]}
                  labelCol={{ span: 24 }}
                >
                  <Input.Password
                    className="custom-input"
                    placeholder={signupPage?.passwordPlaceholder}
                    onChange={(e) => validatePassword(e.target.value)}
                    style={{
                      background: "#ffffff",
                      border: "1px solid rgba(132, 135, 136, 0.25)",
                      height: 50,
                      borderRadius: 12,
                    }}
                  />
                </Form.Item>

                {/* Password validation indicators */}
                <div style={{ marginBottom: "16px", marginTop: "-8px" }}>
                  <ul
                    style={{
                      listStyle: "none",
                      padding: 0,
                      margin: 0,
                      fontSize: "14px",
                    }}
                  >
                    <li
                      style={{
                        color: passwordChecked.otherChecks
                          ? "#039855"
                          : "#3F3F3F",
                        marginBottom: "4px",
                      }}
                    >
                      • {password?.validation_1}
                    </li>
                    <li
                      style={{
                        color: passwordChecked.lengthCheck
                          ? "#039855"
                          : "#3F3F3F",
                      }}
                    >
                      • {password?.validation_2}
                    </li>
                  </ul>
                </div>

                {/* Confirm Password */}
                <Form.Item
                  label={
                    <span className={lang === "ar" ? "ar-label" : ""}>
                      {password?.confirm_password || (lang === "ar" ? "تأكيد كلمة المرور" : "Confirm Password")}
                    </span>
                  }
                  name="confirm_password"
                  dependencies={["password"]}
                  rules={[
                    {
                      required: true,
                      message: password?.confirm_password_error || (lang === "ar" ? "تأكيد كلمة المرور مطلوب" : "Please confirm your password"),
                    },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue("password") === value) {
                          return Promise.resolve()
                        }
                        return Promise.reject(
                          password?.passwordMatch || (lang === "ar" ? "كلمات المرور غير متطابقة!" : "Passwords do not match!")
                        )
                      },
                    }),
                  ]}
                  labelCol={{ span: 24 }}
                >
                  <Input.Password
                    className="custom-input"
                    placeholder={lang === "ar" ? "أعد إدخال كلمة المرور" : "Re-enter your password"}
                    style={{
                      background: "#ffffff",
                      border: "1px solid rgba(132, 135, 136, 0.25)",
                      height: 50,
                      borderRadius: 12,
                    }}
                  />
                </Form.Item>

                {/* Terms & Conditions — same pattern as Login */}
                <div className="login-terms-row">
                  <Form.Item
                    name="agreeToTerms"
                    valuePropName="checked"
                    rules={[
                      {
                        validator: (_, value) =>
                          value
                            ? Promise.resolve()
                            : Promise.reject(loginPage?.agree_error),
                      },
                    ]}
                    className="login-terms-form-item"
                  >
                    <Checkbox className="login-terms-checkbox">
                      <span className="login-terms-text">
                        {loginPage?.agree_prefix}{" "}
                        <button
                          type="button"
                          className="login-terms-link-btn"
                          onClick={(e) => {
                            e.preventDefault()
                            setIsTermsModalOpen(true)
                          }}
                        >
                          {loginPage?.terms_of_service}
                        </button>{" "}
                        {loginPage?.and}{" "}
                        <button
                          type="button"
                          className="login-terms-link-btn"
                          onClick={(e) => {
                            e.preventDefault()
                            setIsPrivacyModalOpen(true)
                          }}
                        >
                          {loginPage?.privacy_policy_link}
                        </button>
                      </span>
                    </Checkbox>
                  </Form.Item>
                </div>

                <Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={loading}
                    disabled={
                      loading ||
                      !passwordChecked.lengthCheck ||
                      !passwordChecked.otherChecks
                    }
                    style={{
                      width: "100%",
                      fontSize: "18px",
                      height: 50,
                      borderRadius: 12,
                      background: "#1d869a",
                      border: "none",
                    }}
                  >
                    {loading ? <Spin /> : signupPage?.buttonText}
                  </Button>
                </Form.Item>
              </>
            )}

            <div
              style={{
                textAlign: "center",
                fontSize: "14px",
                color: "#343c3e",
                marginTop: "8px",
              }}
            >
              {lang === "ar"
                ? "هل لديك حساب بالفعل؟"
                : "Already have an account?"}
              <a
                href="/login"
                style={{
                  color: "#1d869a",
                  fontWeight: "600",
                  textDecoration: "underline",
                  paddingLeft: lang === "ar" ? "" : "5px",
                  paddingRight: lang === "ar" ? "5px" : "",
                }}
              >
                {lang === "ar" ? "تسجيل الدخول" : "Login"}
              </a>
            </div>
          </Form>
        </div>
      </div>
    </>
  )
}

export default Signup
